/**
 * ShareModal — platform-aware sharing sheet
 *
 * Supports: Web Share API (native), Facebook, Twitter/X, WhatsApp, Telegram,
 *           copy-to-clipboard, and QR code generation.
 *
 * Usage:
 *   <ShareModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     url="https://lovelanguagefm.lovable.app"
 *     title="Listen to 'Malume' on Love Language FM!"
 *     description="Heard on Africa's biggest radio station — Ukhozi FM."
 *   />
 */

import { useEffect, useRef, useState, useCallback, forwardRef } from "react";
import { X, Copy, Check, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  description?: string;
  /** Optional CDN thumbnail shown as a preview strip in the modal header */
  thumbnailUrl?: string;
}

// ─── Platform definitions ─────────────────────────────────────────────────────

interface Platform {
  id: string;
  label: string;
  color: string;
  icon: React.ReactNode;
  getHref?: (url: string, title: string) => string;
  onClick?: (url: string, title: string) => void;
}

// Minimal inline SVG brand icons — no external dependencies, GPU-composited
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M22 12c0-5.522-4.477-10-10-10S2 6.478 2 12c0 4.991 3.657 9.128 8.438 9.878V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.22 8.22 0 004.84 1.56V6.8a4.85 4.85 0 01-1.07-.11z"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildPrefilledText(title: string, url: string): string {
  return `🎶 ${title} ${url}`;
}

// ─── Share Button ─────────────────────────────────────────────────────────────

function PlatformBtn({
  label,
  color,
  icon,
  onClick,
}: {
  label: string;
  color: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`Share on ${label}`}
      className="btn-press flex flex-col items-center gap-2 group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-[var(--duration-base)] group-hover:scale-105"
        style={{ background: color, color: "#fff" }}
      >
        {icon}
      </div>
      <span className="font-sans text-[8px] uppercase tracking-[0.18em] text-muted-foreground group-hover:text-foreground transition-colors">
        {label}
      </span>
    </button>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

const ShareModal = forwardRef<HTMLDivElement, ShareModalProps>(function ShareModal({
  open,
  onClose,
  url,
  title = "Absa Life featuring uKhozi FM – Ezingasoze Zabuna.",
  description = "Immortalise your words into a timeless radio classic.",
  thumbnailUrl,
}, _ref) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Reset state when reopened
  useEffect(() => {
    if (open) { setCopied(false); setShowQR(false); }
  }, [open]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: select a temp input
      const el = document.createElement("input");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  }, [url]);

  const nativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url });
      } catch { /* user cancelled */ }
    }
  }, [url, title, description]);

  const openPlatform = useCallback((href: string) => {
    // Opened synchronously from click handler to avoid popup blockers
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=520");
  }, []);

  // Copy link to clipboard then open destination (for platforms without web share intents)
  const copyAndOpen = useCallback(async (destination: string) => {
    try { await navigator.clipboard.writeText(url); } catch { /* silent */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
    setTimeout(() => window.open(destination, "_blank", "noopener,noreferrer"), 150);
  }, [url]);

  const text = buildPrefilledText(title, url);

  const platforms: Platform[] = [
    {
      id: "facebook",
      label: "Facebook",
      color: "#1877F2",
      icon: <FacebookIcon />,
      getHref: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
    },
    {
      id: "twitter",
      label: "X / Twitter",
      color: "#000",
      icon: <TwitterIcon />,
      getHref: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      color: "#25D366",
      icon: <WhatsAppIcon />,
      getHref: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      color: "#2AABEE",
      icon: <TelegramIcon />,
      getHref: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
    },
    {
      id: "instagram",
      label: "Instagram",
      // Instagram brand gradient approximated as a solid coral-pink
      color: "#E1306C",
      icon: <InstagramIcon />,
      // Deep link opens story camera on mobile; falls back to instagram.com on desktop.
      // Link is also copied to clipboard so user can paste it as a story link sticker.
      onClick: (_u) => copyAndOpen("instagram://story-camera"),
    },
    {
      id: "tiktok",
      label: "TikTok",
      color: "#010101",
      icon: <TikTokIcon />,
      // TikTok has no open web-share intent — copy link + open app/site
      onClick: (_u) => copyAndOpen("https://www.tiktok.com/"),
    },
  ];

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center animate-fade-in"
      style={{ background: "hsl(0 0% 0% / 0.6)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-label="Share"
    >
      <div
        className="relative w-full sm:max-w-sm bg-card border border-border rounded-t-2xl sm:rounded-2xl px-6 pt-6 pb-8 animate-scale-in"
        style={{ maxHeight: "90dvh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Thumbnail preview strip */}
        {thumbnailUrl && (
          <div className="relative w-full h-24 -mx-0 mb-5 overflow-hidden rounded-lg">
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
              style={{ filter: "brightness(0.7) saturate(1.1)" }}
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(to top, hsl(var(--card)) 0%, transparent 60%)" }}
            />
            {/* Header overlaid on thumbnail */}
            <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
              <div>
                <p className="font-sans text-[8px] uppercase tracking-[0.25em] text-foreground/50 mb-0.5">
                  Share
                </p>
                <p className="font-serif text-sm text-foreground leading-snug line-clamp-1 drop-shadow">
                  {title}
                </p>
              </div>
              <button
                onClick={onClose}
                className="btn-press w-7 h-7 rounded-full border border-border/60 flex items-center justify-center text-foreground/60 hover:text-foreground transition-colors bg-card/70 backdrop-blur-sm"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Header (no thumbnail) */}
        {!thumbnailUrl && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-sans text-[9px] uppercase tracking-[0.25em] text-muted-foreground/50 mb-0.5">
                Share
              </p>
              <p className="font-serif text-sm text-foreground leading-snug line-clamp-1">
                {title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn-press w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Native share (mobile) */}
        {typeof navigator !== "undefined" && "share" in navigator && (
          <button
            onClick={nativeShare}
            className="btn-press w-full flex items-center justify-center gap-2 border border-absa-red/40 text-absa-red font-sans text-[10px] uppercase tracking-[0.2em] py-2.5 mb-5 hover:bg-absa-red hover:text-primary-foreground transition-all duration-[var(--duration-base)] rounded"
          >
            Share via…
          </button>
        )}

        {/* Platform grid — 3 columns to fit 6 platforms */}
        <div className="grid grid-cols-3 gap-x-2 gap-y-4 mb-6">
          {platforms.map((p) => (
            <PlatformBtn
              key={p.id}
              label={p.label}
              color={p.color}
              icon={p.icon}
              onClick={() => {
                if (p.getHref) openPlatform(p.getHref(url, text));
                else if (p.onClick) p.onClick(url, text);
              }}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-border mb-5" />

        {/* Copy link row */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 min-w-0 bg-background border border-border rounded px-3 py-2">
            <p className="font-sans text-[10px] text-muted-foreground truncate">{url}</p>
          </div>
          <button
            onClick={copyToClipboard}
            className="btn-press flex-shrink-0 flex items-center gap-1.5 border border-border rounded px-3 py-2 font-sans text-[9px] uppercase tracking-[0.15em] transition-all duration-[var(--duration-base)] hover:border-absa-red hover:text-absa-red"
            aria-label="Copy link"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-green-500">Copied</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>

        {/* QR Code toggle */}
        <button
          onClick={() => setShowQR((v) => !v)}
          className="btn-press flex items-center gap-1.5 text-muted-foreground hover:text-foreground font-sans text-[9px] uppercase tracking-[0.15em] transition-colors"
        >
          <QrCode className="w-3 h-3" />
          {showQR ? "Hide QR" : "Show QR code"}
        </button>

        {showQR && (
          <div className="mt-4 flex justify-center animate-fade-in">
            <div className="p-3 bg-background border border-border rounded">
              <QRCodeSVG
                value={url}
                size={140}
                bgColor="transparent"
                fgColor="hsl(var(--foreground))"
                level="M"
              />
              <p className="font-sans text-[7px] uppercase tracking-[0.15em] text-muted-foreground/40 text-center mt-2">
                Scan to open
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ShareModal;

